"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import { Eye, EyeOff, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { LogoMark } from "@/components/Logo";
import { fetchWithCsrf } from "@/lib/api/fetch-csrf";

export default function RegisterPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // Single checkbox covers the age + ToS + no-refund attestation. We
  // capture as one boolean for UX but send the three explicit fields
  // the server schema expects so a "I only meant to confirm age" claim
  // is incompatible with the wire format.
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Live validation flags for the strength checklist
  const rules = {
    length: password.length >= 8,
    mix: /[a-zA-Z]/.test(password) && /[0-9]/.test(password),
    match: password.length > 0 && password === confirmPassword,
  };
  const allValid = rules.length && rules.mix && rules.match && accepted;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!rules.length) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!rules.match) {
      setError("Passwords don't match.");
      return;
    }
    if (!accepted) {
      setError("Please confirm the age, terms, and no-refund acknowledgement to continue.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetchWithCsrf("/api/v1/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          acceptedAge: true,
          acceptedTos: true,
          acceptedRefundPolicy: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? "Registration failed");
        return;
      }
      router.push("/dashboard");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex justify-center pt-8">
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center text-center">
          <LogoMark className="h-10 w-10 mb-2" />
          <CardTitle>Create your account</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <Alert>{error}</Alert>}

            <div className="space-y-1">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="confirmPassword">Confirm password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  className={cn(
                    "pr-10",
                    confirmPassword.length > 0 && !rules.match && "border-destructive focus-visible:ring-destructive/30",
                    rules.match && "border-emerald-400",
                  )}
                />
                {rules.match && (
                  <Check size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-success-fg" />
                )}
              </div>
              {confirmPassword.length > 0 && !rules.match && (
                <p className="text-xs text-destructive">Passwords don&apos;t match</p>
              )}
            </div>

            {/* Live password strength checklist */}
            {password.length > 0 && (
              <div className="rounded-lg bg-muted/40 border border-border px-3 py-2.5 space-y-1.5">
                <Rule ok={rules.length} label="At least 8 characters" />
                <Rule ok={rules.mix}    label="Mix of letters and numbers" />
                <Rule ok={rules.match}  label="Passwords match" />
              </div>
            )}

            {/* Age + Terms + no-refund acknowledgement. Single combined
                checkbox the user has to actively tick — server enforces
                via z.literal(true) on three separate booleans so a
                partial-accept request is impossible. Age threshold is
                the global 18+ floor here; the per-country override
                (if any) fires at /sell once a country has been picked.
                Both links open in a new tab so the user doesn't lose
                the half-filled form. */}
            <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border accent-primary shrink-0 cursor-pointer"
                aria-describedby="acknowledgement-help"
              />
              <span id="acknowledgement-help" className="leading-snug">
                I confirm I am at least <strong>18 years old</strong> and accept our{" "}
                <Link href="/legal/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link href="/legal/refunds" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                  Refund Policy
                </Link>
                .
              </span>
            </label>

            <Button type="submit" className="w-full" disabled={loading || !allValid}>
              {loading ? "Creating account…" : "Create account"}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link href="/auth/login" className="underline hover:text-foreground">
                Sign in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function Rule({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span
        className={cn(
          "inline-flex h-4 w-4 rounded-full items-center justify-center border shrink-0 transition-colors",
          ok ? "bg-success-bg border-success-border text-success-fg" : "bg-background border-border text-muted-foreground"
        )}
      >
        {ok ? <Check size={9} strokeWidth={3} /> : <span className="h-1 w-1 rounded-full bg-current" />}
      </span>
      <span className={cn(ok ? "text-foreground" : "text-muted-foreground")}>{label}</span>
    </div>
  );
}
